import { useEffect, useRef, useCallback } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from 'react-responsive'
// import noUiSlider from 'nouislider'
// import { nouislider } from 'surveyjs-widgets'
// import * as SurveyCore from 'survey-core'
import 'nouislider/dist/nouislider.css'
import CryptoJS from 'crypto-js'
import DOMPurify from 'dompurify'
// nouislider(SurveyCore)
import { useSurveyAnswerConverter } from './useSurveyAnswerConverter'

export function useSubmitAnswers({
  survey,
  pid,
  ac,
  tk,
  imagesReadingTime,
  setIsLoading,
  helpFunction,
  canShowPrevButtonPages,
  updateStringComponents,
  choicesOrderRef,
  choiceIndexMapRef,
  endSurveyMsg,
  bringOptionsMapRef,
  haAccessToken,
  haMemberMissionId,
  setIsDkabio,
  // 暫存需加
  requiresLogin,
  userIdRef,
  enableProgressSaving = false,
  sfdSamplerId,
  webAuthType = 0,
}) {
  const navigate = useNavigate()
  const isMobile = useMediaQuery({ query: '(max-width: 576px)' })

  // other 跟 none 請搜尋 othernone順序

  const timestamps = {}
  const transformedData = useRef([])
  // 帶入題目文字用的 ref(保存所有題目的原始 title)
  const originalTitlesRef = useRef({})

  const host = process.env.REACT_APP_HOST
  // 使⽤者上傳填答結果
  const SubmitAnswersUrl = '/clientWeb/v1/SubmitAnswers'
  const AnswerObjectUpload = '/clientWeb/v1/AnswerObjectUpload'
  const haApiKey = process.env.REACT_APP_HA_API_KEY
  const haApiSecret = process.env.REACT_APP_HA_API_SECRET
  const haApiUrl = process.env.REACT_APP_HA_API_URL
  // const [isLoading, setIsLoading] = useState(false)
  // 上傳檔案
  let uploadCompleted = false
  // discord
  async function sendDiscordNotification(message) {
    try {
      await axios.post('/api/send-notification', {
        content: message,
      })
    } catch (error) {
      console.error('Failed', error)
    }
  }

  // 選項順序排列
  // const choicesOrderRef = useRef({})
  // const choiceIndexMapRef = useRef({})

  // 核心函數:從原始文字替換所有 {qX-text} 變數
  const replaceVariablesInText = useCallback((survey, originalText) => {
    let updatedText = originalText

    // 支援兩種格式:
    // 1. {q1-text} - 單一題目
    // 2. {q2-20-text} - 範圍題目 (q2到q20之間有值的第一題)
    const singlePattern = /\{q(\d+)-text\}/g
    const rangePattern = /\{q(\d+)-(\d+)-text\}/g

    // 先處理範圍語法
    const rangeMatches = [...originalText.matchAll(rangePattern)]
    rangeMatches.forEach((match) => {
      const fullMatch = match[0] // 例如: {q2-20-text}
      const startNum = parseInt(match[1]) // 例如: 2
      const endNum = parseInt(match[2]) // 例如: 20

      // ✨ 轉換為索引:題目編號 - 1
      const startIndex = startNum - 1 // 2 - 1 = 1 (q1)
      const endIndex = endNum - 1 // 20 - 1 = 19 (q19)

      // 在範圍內找第一個有值的題目
      let displayText = ''
      for (let i = startIndex; i <= endIndex; i++) {
        const questionName = `q${i}`
        const question = survey.getQuestionByName(questionName)
        const value = survey.getValue(questionName)

        if (question && value !== undefined && value !== null) {
          // 找到有值的題目了
          displayText = getDisplayTextFromValue(question, value)
          if (displayText) {
            console.log(
              `✅ 範圍 {q${startNum}-${endNum}-text} (索引 q${startIndex}-q${endIndex}) 中找到 ${questionName} 的值: ${displayText}`
            )
            break
          }
        }
      }

      if (displayText) {
        updatedText = updatedText.replace(
          fullMatch,
          `<strong>${displayText}</strong>`
        )
      }
    })

    // 再處理單一題目語法
    const singleMatches = [...updatedText.matchAll(singlePattern)]
    singleMatches.forEach((match) => {
      const fullMatch = match[0] // 例如: {q1-text}
      const questionNumber = match[1] // 例如: 1
      // ✨ 轉換為索引:題目編號 - 1
      const questionIndex = questionNumber - 1 // 1 - 1 = 0
      const refQuestionName = `q${questionIndex}`

      const refQuestion = survey.getQuestionByName(refQuestionName)
      const refValue = survey.getValue(refQuestionName)

      if (!refQuestion || refValue === undefined || refValue === null) {
        return
      }

      const displayText = getDisplayTextFromValue(refQuestion, refValue)

      if (displayText) {
        updatedText = updatedText.replace(
          fullMatch,
          `<strong>${displayText}</strong>`
        )
      }
    })

    return updatedText
  }, [])

  // 輔助函數:從問題和值取得顯示文字
  function getDisplayTextFromValue(question, value) {
    let displayText = ''

    if (Array.isArray(value)) {
      // 多選題
      displayText = value
        .map((val) => {
          const choice = question.choices.find((c) => c.value === val)
          if (!choice) return ''
          return choice.text
        })
        .filter((text) => text)
        .join('、')
    } else {
      // 單選題
      const choice = question.choices.find((c) => c.value === value)
      if (choice) {
        displayText = choice.text
      }
    }

    return displayText
  }

  // 更新所有包含變數的題目
  const updateQuestionTextsWithVariables = useCallback(
    (survey, changedQuestionName) => {
      const changedQuestionIndex = parseInt(
        changedQuestionName.replace('q', '')
      )

      survey.getAllQuestions().forEach((question) => {
        const originalTitle = originalTitlesRef.current[question.name]
        if (!originalTitle) return

        let needsUpdate = false

        // ✨ 檢查是否包含單一題目變數 {qX-text}
        // 需要將索引轉回題目編號來比對
        const questionNumber = changedQuestionIndex + 1 // 0 + 1 = 1
        const singlePattern = new RegExp(`\\{q${questionNumber}-text\\}`)
        if (singlePattern.test(originalTitle)) {
          needsUpdate = true
        }

        // ✨ 檢查是否包含範圍變數 {qX-Y-text}
        const rangePattern = /\{q(\d+)-(\d+)-text\}/g
        const rangeMatches = [...originalTitle.matchAll(rangePattern)]
        rangeMatches.forEach((match) => {
          const startNum = parseInt(match[1])
          const endNum = parseInt(match[2])
          // 如果變更的題目在範圍內
          if (
            changedQuestionIndex >= startNum &&
            changedQuestionIndex <= endNum
          ) {
            needsUpdate = true
          }
        })

        if (!needsUpdate) return

        // 從原始模板重新替換
        const updatedTitle = replaceVariablesInText(survey, originalTitle)
        question.title = updatedTitle

        console.log(`🔄 更新 ${question.name}:`, updatedTitle)
      })
    },
    [replaceVariablesInText]
  )

  // 拿來存 other 選項的 index
  const otherAnswers = {}
  const noneAnswers = {}
  const matrixAnswers = {}

  // 使用新的答案轉換 hook
  const { createProgressData, saveProgressAPI } = useSurveyAnswerConverter({
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers,
    noneAnswers,
    matrixAnswers,
    timestamps,
    survey,
  })

  // 檢查是否存在帶有 'imgWrap50' className 的元素
  function hideSdBtnIfImgWrapExists() {
    if (document.querySelector('.imgWrap50')) {
      // 如果存在，找到所有帶有 'sd-btn' className 的元素
      const sdBtnElements = document.querySelectorAll('.sd-btn')
      const thirtySecondDelete = document.querySelectorAll(
        '.thirtySecondDelete'
      )

      // 遍歷每一個 'sd-btn' 元素並設置其 display 為 'none'
      sdBtnElements.forEach((element) => {
        element.style.display = 'none'
      })

      // 30秒後恢復 'sd-btn' 元素的顯示
      setTimeout(() => {
        sdBtnElements.forEach((element) => {
          element.style.display = ''
        })
        thirtySecondDelete.forEach((element) => {
          element.style.display = 'none'
        })
        // TODO:
      }, imagesReadingTime * 1000) // 30秒
    }
  }

  // 初始化時間戳記
  const initializeTimestamps = (survey) => {
    Object.keys(survey.data).forEach((questionName) => {
      if (!timestamps[questionName]) {
        timestamps[questionName] = Date.now()
      }
    })
  }

  // 儲存進度函數
  const saveProgress = useCallback(
    async (surveyData) => {
      if (
        !enableProgressSaving ||
        (!requiresLogin?.login && !sfdSamplerId && !(webAuthType > 0))
      )
        return

      try {
        const progressData = createProgressData(surveyData)
        await saveProgressAPI(progressData)
      } catch (error) {
        console.error('儲存進度失敗:', error)
      }
    },
    [
      enableProgressSaving,
      requiresLogin,
      sfdSamplerId,
      webAuthType,
      createProgressData,
      saveProgressAPI,
      // userIdRef,
    ]
  )

  useEffect(() => {
    if (!survey) return

    const onMd = (_, opt) => {
      let t = opt.text || ''

      // 紅色
      t = t
        .replace(
          /<red>(.*?)<\/red>/gi,
          '<span class="custom-red-text">$1</span>'
        )
        .replace(
          /&lt;red&gt;(.*?)&lt;\/red&gt;/gi,
          '<span class="custom-red-text">$1</span>'
        )

      // 藍色
      t = t
        .replace(
          /<blue>(.*?)<\/blue>/gi,
          '<span class="custom-blue-text">$1</span>'
        )
        .replace(
          /&lt;blue&gt;(.*?)&lt;\/blue&gt;/gi,
          '<span class="custom-blue-text">$1</span>'
        )

      // 灰色
      t = t
        .replace(
          /<gray>(.*?)<\/gray>/gi,
          '<span class="custom-gray-text">$1</span>'
        )
        .replace(
          /&lt;gray&gt;(.*?)&lt;\/gray&gt;/gi,
          '<span class="custom-gray-text">$1</span>'
        )

      // 粗體
      t = t
        .replace(/<b>(.*?)<\/b>/gi, '<strong>$1</strong>')
        .replace(/&lt;b&gt;(.*?)&lt;\/b&gt;/gi, '<strong>$1</strong>')

      // 底線
      t = t
        .replace(/<u>(.*?)<\/u>/gi, '<u>$1</u>')
        .replace(/&lt;u&gt;(.*?)&lt;\/u&gt;/gi, '<u>$1</u>')

      // 新增說明 [[i]]
      if (t.includes('[[i]]')) {
        const parts = t.split('[[i]]')
        if (parts.length > 1) {
          const mainTitle = parts[0].trim()
          const description = parts[1].trim()
          t = `${mainTitle}<details class="info-details"><summary class="info-summary"><span class="info-icon">i</span></summary><div class="info-content">${description}</div></details>`
        }
      }

      // 自訂連結語法: [連結文字](https://example.com)
      t = t.replace(
        /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      )

      opt.html = DOMPurify.sanitize(t, {
        ALLOWED_TAGS: [
          'span',
          'strong',
          'u',
          'p',
          'br',
          'details',
          'summary',
          'div',
          'a',
          'img',
        ],
        ALLOWED_ATTR: [
          'class',
          'style',
          'href',
          'target',
          'rel',
          'src',
          'alt',
          'width',
          'height',
        ],
        ALLOWED_URI_REGEXP: /^https?:\/\//i,
      })
    }

    survey.onTextMarkdown.add(onMd)
    return () => survey.onTextMarkdown.remove(onMd)
  }, [survey])

  useEffect(() => {
    // 先檢查 survey 是否存在
    if (!survey) {
      return
    }

    // 初始化:保存所有原始 title
    survey.getAllQuestions().forEach((question) => {
      if (!originalTitlesRef.current[question.name]) {
        originalTitlesRef.current[question.name] = question.title
        // console.log(`💾 保存 ${question.name} 原始題目:`, question.title)
      }
    })

    initializeTimestamps(survey)

    const handleAfterRenderQuestion = (survey, options) => {
      // ============== othernone順序 ==============
      // 將 other 文字框移到 other 選項後面，none 選項前面
      // const question = options.question

      // // 檢查是否已選中 other
      // const hasOther = Array.isArray(question.value)
      //   ? question.value.includes('other')
      //   : question.value === 'other'

      // if (hasOther && question.showOtherItem && question.showNoneItem) {
      //   setTimeout(() => {
      //     const container = options.htmlElement
      //     const otherDiv = container
      //       .querySelector('input[value="other"]')
      //       ?.closest('.sd-checkbox')
      //     const noneDiv = container.querySelector('.sd-checkbox--none')
      //     const otherTextarea = container.querySelector(
      //       '.sd-question__comment-area'
      //     )

      //     if (otherDiv && noneDiv && otherTextarea) {
      //       otherDiv.insertAdjacentElement('afterend', otherTextarea)
      //     }
      //   }, 50)
      // }
      // ⬆⬆⬆⬆⬆⬆⬆⬆ othernone順序 ⬆⬆⬆⬆⬆⬆⬆⬆

      // 每次渲染時,從原始模板替換變數
      const originalTitle = originalTitlesRef.current[options.question.name]
      if (originalTitle) {
        const updatedTitle = replaceVariablesInText(survey, originalTitle)
        if (updatedTitle !== originalTitle) {
          options.question.title = updatedTitle
          console.log(`📝 渲染時更新 ${options.question.name}:`, updatedTitle)
        }
      }

      // 專有名詞備註
      if (typeof updateStringComponents === 'function') {
        // 如果存在，调用该函数
        updateStringComponents(survey, options)
      }
      hideSdBtnIfImgWrapExists()

      // [[i]] 產生的 .info-details 留在 sv-string-viewer 內由 onMd 自然 render。
      // 不再手動搬移到 titleContainer (會跟 React reconcile 打架，而且 sv-string-viewer
      // 自己 re-render 時也不會清掉搬出去的舊 icon，反而造成重複)。
      // 預設 questionTitlePattern = "numTitleRequire"，asterisk 在 sv-string-viewer
      // 後面，自然順序就是 [編號][文字][icon][星號]。

      // 圖片直的橫的影響他的大小
      const images = document.querySelectorAll('img')
      Array.from(images).forEach((image) => {
        image.addEventListener('load', () => fitImage(image))

        if (image.complete && image.naturalWidth !== 0) fitImage(image)
      })

      function fitImage(image) {
        const aspectRatio = image.naturalWidth / image.naturalHeight
        // If image is portrait
        if (aspectRatio < 1 && !isMobile) {
          // console.log('直式圖片')
          // image.style.width = '60%'
        }
      }

      // 顯示上一頁按鈕
      // canShowPrevButtonPages: null = 全部不可翻頁、'all' = 全部可翻頁、陣列 = 指定頁碼可翻頁
      if (canShowPrevButtonPages !== null) {
        const prevBtn = document.querySelector('#sv-nav-prev')
        const page = options.question.page
        const pageName = page ? page.name : null
        if (prevBtn && canShowPrevButtonPages === 'all') {
          prevBtn.style.display = 'block'
          return
        }
        if (prevBtn) {
          for (let i = 0; i < canShowPrevButtonPages.length; i++) {
            if (pageName === 'page' + canShowPrevButtonPages[i]) {
              prevBtn.style.display = 'block'
              return
            } else {
              prevBtn.style.display = 'none'
            }
          }
        }
      }
    }

    // ============== othernone順序 ==============
    // 找到 other 和 none 選項的索引
    // const findSpecialOptionsIndexes = (question) => {
    //   const visibleChoices = question.visibleChoices
    //   let otherIndex = -1
    //   let noneIndex = -1

    //   // 直接從實際顯示的選項中找到 other 和 none 的位置
    //   visibleChoices.forEach((choice, index) => {
    //     if (choice.value === 'other') {
    //       otherIndex = index
    //     }
    //     if (choice.value === 'none') {
    //       noneIndex = index
    //     }
    //   })

    //   return { otherIndex, noneIndex }
    // }
    // ⬆⬆⬆⬆⬆⬆⬆⬆ othernone順序 ⬆⬆⬆⬆⬆⬆⬆⬆

    const handleValueChanged = (survey, options) => {
      // ============== othernone順序 ==============
      // 當選中 other 選項時，文字框會動態出現
      // if (
      //   options.value === 'other' ||
      //   (Array.isArray(options.value) && options.value.includes('other'))
      // ) {
      //   // 延遲執行，確保文字框已經渲染
      //   setTimeout(() => {
      //     const container = document.querySelector(`#${options.question.id}`)
      //     if (!container) return

      //     const otherDiv = container
      //       .querySelector('input[value="other"]')
      //       ?.closest('.sd-checkbox')
      //     const noneDiv = container.querySelector('.sd-checkbox--none')
      //     const otherTextarea = container.querySelector(
      //       '.sd-question__comment-area'
      //     )

      //     if (otherDiv && noneDiv && otherTextarea) {
      //       // 將文字框移到 other 選項後面，none 選項前面
      //       otherDiv.insertAdjacentElement('afterend', otherTextarea)
      //     }
      //   }, 0) // 給 Survey.js 一點時間渲染文字框
      // }
      // ⬆⬆⬆⬆⬆⬆⬆⬆ othernone順序 ⬆⬆⬆⬆⬆⬆⬆⬆

      helpFunction(survey, options)

      // 選項文字帶入題目{q1-text}
      updateQuestionTextsWithVariables(survey, options.name)

      let order = options.question.visibleChoices
      let matrixOrder = options.question.columns

      // 帶入帶出
      if (bringOptionsMapRef?.current.hasOwnProperty(options.name)) {
        // 篩選變下一題的選項

        // 帶入目標問題(後)
        const targetQuestionName = bringOptionsMapRef.current[options.name]
        const targetQuestion = survey.getQuestionByName(targetQuestionName)
        // 帶出來源問題(前)
        const sourceQuestion = survey.getQuestionByName(options.name)
        // 原始問題編號(前)
        const origin_qi = Number(options.name.replace('q', ''))
        let choices = []

        // 如果選擇的是 none，跳過帶入邏輯

        if (Array.isArray(options.value)) {
          // 處理複選題
          options.value.forEach(function (choiceValue, index) {
            if (typeof choiceValue === 'number') {
              const foundChoice = sourceQuestion.choices.find(
                (choice) => choice.value === choiceValue
              )
              const choiceText = foundChoice ? foundChoice.text : choiceValue
              // console.log('choiceValue', choiceValue)
              choices.push({
                value: index,
                text: choiceText,
                origin_op: choiceValue,
                origin_qi: origin_qi,
              })
            } else if (choiceValue === 'other') {
              choices.push({
                value: index,
                text: '其他(上題填寫內容)',
                origin_op: choiceValue,
                origin_qi: origin_qi,
              })
            } else if (choiceValue === 'none') {
              console.log(
                'options.question.visibleChoices',
                options.question.visibleChoices
              )
              const noneIndex = options.question.visibleChoices.findIndex(
                (c) =>
                  c.text === options.question.noneText || c.value === 'none'
              )
              console.log('noneIndex', noneIndex)
              if (noneIndex !== -1) {
                console.log('none', {
                  value: noneIndex,
                  text: options.question.noneText,
                  origin_op: choiceIndexMapRef.current[options.name]?.none,
                  origin_qi: origin_qi,
                })
                choices.push({
                  value: noneIndex,
                  text: options.question.noneText,
                  origin_op: choiceIndexMapRef.current[options.name]?.none,
                  origin_qi: origin_qi,
                })
              }
            }
          })

          console.log('choices', choices)
        } else if (
          typeof options.value === 'object' &&
          options.value !== null
        ) {
          // 矩陣題不需要處理帶入選項，直接 return 或跳過
          Object.entries(options.value).forEach(([rowKey, colValue]) => {
            return
          })
        }

        targetQuestion.choices = choices

        // 最後回傳的 order 要改
        // const order = targetQuestion.visibleChoices
        // if (order) {
        //   // 將選項順序設為每個選項的索引值
        //   choiceIndexMapRef.current[targetQuestionName] = order.reduce(
        //     (map, choice, index) => {
        //       const correspondingChoice = choices.find(
        //         (c) => c.text === choice.title
        //       )
        //       map[index] = {
        //         index: index,
        //         title: choice.title,
        //         origin_op: correspondingChoice
        //           ? correspondingChoice.origin_op
        //           : null,
        //       }
        //       return map
        //     },
        //     {}
        //   )
        //   // console.log('choiceIndexMapRef', choiceIndexMapRef)
        // }
        choiceIndexMapRef.current[targetQuestionName] = choices.reduce(
          (map, choice, index) => {
            map[index] = {
              index,
              title: choice.text, // 注意：是 text 不是 title
              origin_op: choice.origin_op,
              origin_qi: origin_qi,
            }
            return map
          },
          {}
        )

        console.log(
          '✅ confirm indexMapRef',
          targetQuestionName,
          choiceIndexMapRef.current[targetQuestionName]
        )
      }

      if (order) {
        let orderMap = order.map((choice) => choice.value)
        choicesOrderRef.current[options.question.name] = orderMap
        // 給 SurveyJS 一點時間完成初始化
        setTimeout(() => {
          if (orderMap.some((value) => value != null)) {
            choicesOrderRef.current[options.question.name] = orderMap
          }
        }, 0)
        // 建立一個物件來存儲當前問題的值和索引的對應關系
        // [FIX 9EZ-200] 改為 merge：useConvertSurvey 的 pre-populate 會先寫
        // {none, other} 到這個 key，舊版 guard `!current` 永遠跳過，導致一般
        // 選項的 value→displayIndex 對應不會被建立，送出時 ans 變成原始 value
        // 而非畫面顯示順序。改為每次重建並保留 pre-populate 的 .none/.other。
        const existing = choiceIndexMapRef.current[options.question.name] || {}
        choiceIndexMapRef.current[options.question.name] = order.reduce(
          (map, choice, index) => {
            // bringOptions 帶入的 slot 是 {index, title, origin_op, origin_qi}
            // 不要用純 number 覆蓋（會導致 submit 時 bringing_options 漏掉）
            const prev = map[choice.value]
            if (prev && typeof prev === 'object' && prev.title) {
              map[choice.value] = { ...prev, index }
            } else {
              map[choice.value] = index
            }
            return map
          },
          { ...existing }
        )
        // console.log('choiceIndexMapRef', choiceIndexMapRef)
        // console.log('choicesOrderRef', choicesOrderRef)

        // 只有 other
        if (orderMap.includes('other') && orderMap.indexOf('none') === -1) {
          const otherIndex = orderMap.indexOf('other')
          orderMap[otherIndex] = orderMap.length - 1
          choicesOrderRef.current[options.question.name] = orderMap
        }

        // 只有 none
        if (orderMap.includes('none') && orderMap.indexOf('other') === -1) {
          const noneIndex = orderMap.indexOf('none')
          orderMap[noneIndex] = orderMap.length - 1
          choicesOrderRef.current[options.question.name] = orderMap
        }

        if (orderMap.includes('other') && orderMap.includes('none')) {
          const otherIndex = orderMap.indexOf('other')
          const noneIndex = orderMap.indexOf('none')
          // 因應 none 要在最後一個
          orderMap[otherIndex] = orderMap.length - 1
          orderMap[noneIndex] = orderMap.length - 2
          // ============== othernone順序 ==============
          // orderMap[otherIndex] = orderMap.length - 2
          // orderMap[noneIndex] = orderMap.length - 1
          // ⬆⬆⬆⬆⬆⬆⬆⬆ othernone順序 ⬆⬆⬆⬆⬆⬆⬆⬆
          choicesOrderRef.current[options.question.name] = orderMap
        }
      } else {
        choicesOrderRef.current[options.question.name] = []
        choiceIndexMapRef.current[options.question.name] = {}
      }
      // 矩陣 order（子題隨機順序）
      if (matrixOrder && matrixOrder.length > 0) {
        // 使用 rows 的顯示順序作為 ord（反映子題隨機後的實際顯示順序）
        const question = options.question
        if (question.rows && question.rows.length > 0) {
          const matrixRowsOrder = question.rows.map((v) => v.value)
          choicesOrderRef.current[options.question.name] = matrixRowsOrder
        } else {
          const matrixChoicesOrder = matrixOrder.map((v) => v.value)
          choicesOrderRef.current[options.question.name] = matrixChoicesOrder
        }
      }

      // 登記時間
      timestamps[options.name] = Date.now()
      // 改 other
      const question = options.question
      // ======================== 矩陣 =============================
      if (question.rows && question.rows.length > 0) {
        // console.log('矩陣 question', question)

        let value = question.value
        // console.log('矩陣 value', value)
        if (value) {
          // 取得所有行 (rows) 包括被隱藏的
          const allRows = question.rows
          const mapedArrValue = []

          // 遍歷所有行加放答案
          allRows.forEach((row) => {
            const rowValue = row.value
            if (value[rowValue] !== undefined) {
              // 如果有答案,放入答案
              mapedArrValue.push([value[rowValue]])
            } else {
              // 如果沒有答案 (被隱藏或未填答),放入空陣列佔位
              mapedArrValue.push([])
            }
          })

          matrixAnswers[question.name] = mapedArrValue
        }

        // console.log('矩陣 matrixAnswers', matrixAnswers)
      }
      // ============== othernone順序 ==============
      // if (question.showOtherItem || question.showNoneItem) {
      //   const { otherIndex, noneIndex } = findSpecialOptionsIndexes(question)

      //   if (otherIndex !== -1) {
      //     otherAnswers[question.name] = { otherIndex }
      //   }
      //   if (noneIndex !== -1) {
      //     noneAnswers[question.name] = { noneIndex }
      //   }
      // }
      // ⬆⬆⬆⬆⬆⬆⬆⬆ othernone順序 ⬆⬆⬆⬆⬆⬆⬆⬆

      // ============== othernone順序(以下是舊版邏輯可以刪掉) ==============
      if (question.showOtherItem) {
        let value = question.value

        // 複選 other
        if (value && Array.isArray(value) && value.indexOf('other') !== -1) {
          //  OtherIndex 要存在 otherAnswers裡
          //  Other 內容要在complete才會有==
          otherAnswers[question.name] = {
            otherIndex:
              question.choices.length + (question.showNoneItem ? 1 : 0),
          }
        }

        // 單選 other
        if (value && value === 'other') {
          otherAnswers[question.name] = {
            otherIndex:
              question.choices.length + (question.showNoneItem ? 1 : 0),
          }
        }
      }
      if (question.showNoneItem) {
        let value = question.value

        if (value && value.indexOf('none') !== -1) {
          noneAnswers[question.name] = {
            noneIndex: question.choices.length,
          }
        }
      }
    }

    // 頁面變更處理（用於進度儲存）
    const handlePageChanged = (sender, options) => {
      console.log('handlePageChanged', sender.data)
      if (
        enableProgressSaving &&
        (requiresLogin?.login || !!sfdSamplerId || webAuthType > 0) &&
        sender.currentPageNo > 0
      ) {
        saveProgress(sender.data)
      }
    }

    // let [uploadFailed, setuploadFailed] = useState(false)
    let uploadFailed = false
    const handleCompleting = async (sender, options) => {
      let surveyData = sender.data
      // console.log('surveyData', surveyData)
      // 留著有用可以用
      // const allQuestions = survey.getAllQuestions()
      // console.log(
      //   'survey.getAllQuestions()',
      //   allQuestions.forEach(function (question) {
      //     console.log('問題名：', question.name)
      //     console.log('問題類型：', question.getType())
      //     console.log('答案：', survey.data[question.name])
      //     console.log('isRandom', question.areChoicesRandomized)
      //   })
      // )
      // console.log('survey.choiceIndexMap', survey.choiceIndexMap)
      // 建立一個物件來存儲用戶的答案的索引
      const answerIndexes = {}
      // 上傳檔案的 promises
      //
      const uploadPromises = []

      // 【雙重保險】雖然已經透過 onVisibleChanged 即時清除了隱藏題目的答案，
      // 但為了防止極端情況或未觸發事件的預設髒資料，在交卷前再次徹底過濾不可見的題目。
      const visibleQuestions = new Set()
      sender.getAllQuestions().forEach((question) => {
        if (question.isVisible) {
          visibleQuestions.add(question.name)
        }
      })
      console.log('可見題目:', Array.from(visibleQuestions))

      for (let questionName in surveyData) {
        // 只處理可見題目(Comment 是附屬於 other 的,所以也要檢查原題目是否可見)
        if (questionName.includes('-Comment')) {
          // 對於 Comment 題,檢查原題目是否可見
          const actualKey = questionName.split('-')[0]
          if (!visibleQuestions.has(actualKey)) {
            console.log(`跳過不可見題目的 Comment: ${questionName}`)
            delete surveyData[questionName] // 清除看不見的答案
            continue
          }
        } else if (!visibleQuestions.has(questionName)) {
          console.log(`跳過不可見題目: ${questionName}`)
          delete surveyData[questionName] // 清除看不見的答案
          continue
        }

        if (choiceIndexMapRef.current[questionName]) {
          // 獲取當前問題的答案的值
          let answerValue = surveyData[questionName]

          // 帶入答案
          const question = survey.getQuestionByName(questionName)

          if (
            question &&
            Array.isArray(question.choices) &&
            question.choices[0]?.origin_op !== undefined
          ) {
            const enriched = question.choices.map((c) => ({
              o_type: 0,
              na: c.text,
              origin_op: c.origin_op,
              origin_qi: c.origin_qi,
            }))

            const selectedValues = Array.isArray(value) ? value : [value]
            const choiceValues = question.visibleChoices.map((c) => c.value)
            console.log('selectedValues', selectedValues)
            console.log('choiceValues', choiceValues)

            // 若使用者有選 none，補進一筆
            const value = question.value
            if (
              question.showNoneItem &&
              selectedValues.some((v) => {
                const choice = question.visibleChoices.find(
                  (c) => c.value === v
                )
                return (
                  choice?.value === 'none' || choice?.text === question.noneText
                )
              })
            ) {
              enriched.push({
                o_type: 0,
                na: question.noneText || '以上皆無',
                origin_op: choiceValues,
                origin_qi: Number(questionName.replace('q', '')),
              })
            }

            surveyData[`${questionName}_options`] = enriched
            console.log('enriched', enriched)
          }

          // console.log('answerValue', answerValue)
          // 檢查 answerValue 是否是一個陣列
          if (Array.isArray(answerValue)) {
            // 如果是陣列，則對每個元素找到其索引
            if (answerValue[0]?.content) {
              // 有檔案的情況
              const mimeSubtype =
                (answerValue[0].type || '').split('/')[1]?.toUpperCase() || ''
              const fileBody = {
                pid: pid,
                qid: Number(questionName.slice(1)),
                content: {
                  type: mimeSubtype === 'JPEG' ? 'JPG' : mimeSubtype,
                  raw: answerValue[0].content.split(',')[1],
                },
              }

              const uploadPromise = axios
                .post(host + AnswerObjectUpload, fileBody, {
                  headers: {
                    'Content-Type': 'text/plain; charset=UTF-8',
                    Accept: '*/*',
                    ac: ac,
                    tk: tk,
                  },
                })
                .then((res) => {
                  // console.log('res')

                  // 格式錯誤
                  if (res.data.code === '05000') {
                    navigate(`/${pid}/formatError`)
                    throw new Error('Format error')
                    // uploadFailed = true
                    // setuploadFailed(true)
                  }
                  answerIndexes[questionName] = res.data.name
                  return res.data
                })
                .catch((e) => {
                  console.log(e)
                  // setuploadFailed(true)
                  navigate(`/${pid}/formatError`)
                  // console.log('hihi')
                  throw new Error('Format error')
                  // alert('圖片格式不符，僅接受 20MB 以下 JPG/PNG')
                  // sendDiscordNotification(
                  //   `EZC Web AnswerObjectUpload Error \n Error: ${e}  \n surveyData: ${JSON.stringify(
                  //     surveyData
                  //   )} \n Response: ${e.response?.data?.msg} \n Response Code: ${
                  //     e.response?.data?.code
                  //   }`
                  // )
                })
                .finally(() => {
                  setIsLoading(false)
                })

              uploadPromises.push(uploadPromise)
            } else {
              answerIndexes[questionName] = answerValue.map((value) => {
                // 添加安全檢查
                const indexMap = choiceIndexMapRef.current[questionName]
                console.log('安全檢查 indexMap', indexMap)
                if (!indexMap) return value

                const mappedValue = indexMap[value]
                if (typeof mappedValue === 'number') {
                  return mappedValue
                } else if (mappedValue?.index !== undefined) {
                  return mappedValue.index
                } else {
                  return value
                }
              })
            }
          } else if (
            typeof answerValue === 'string' &&
            answerValue.startsWith('data:image/')
          ) {
            // 簽名題 (q_type=10 signaturepad)：上傳 base64 圖片，取回連結
            const mimeMatch = answerValue.match(/data:image\/(\w+);base64,/)
            const ext = mimeMatch
              ? mimeMatch[1].toUpperCase() === 'JPEG'
                ? 'JPG'
                : mimeMatch[1].toUpperCase()
              : 'PNG'
            const raw = answerValue.split(',')[1]
            const fileBody = {
              pid: pid,
              qid: Number(questionName.slice(1)),
              content: { type: ext, raw },
            }
            const uploadPromise = axios
              .post(host + AnswerObjectUpload, fileBody, {
                headers: {
                  'Content-Type': 'text/plain; charset=UTF-8',
                  Accept: '*/*',
                  ac: ac,
                  tk: tk,
                },
              })
              .then((res) => {
                if (res.data.code === '05000') {
                  navigate(`/${pid}/formatError`)
                  throw new Error('Format error')
                }
                answerIndexes[questionName] = res.data.name
                return res.data
              })
              .catch((e) => {
                console.log(e)
                navigate(`/${pid}/formatError`)
                throw new Error('Format error')
              })
              .finally(() => {
                setIsLoading(false)
              })
            uploadPromises.push(uploadPromise)
          } else {
            // 如果不是陣列，則直接找到答案的索引
            answerIndexes[questionName] =
              choiceIndexMapRef.current[questionName][answerValue]
          }
        }
      }

      // Await all file upload promises
      // await Promise.all(uploadPromises)
      try {
        await Promise.all(uploadPromises)
        uploadCompleted = true
      } catch (e) {
        console.error(e)
        uploadFailed = true // 设置失败标志
      }

      if (uploadFailed) {
        options.allowComplete = false
      } else {
        // console.log('surveyData', surveyData)
        let newTransformedData = Object.entries(surveyData).reduce(
          (acc, [key, value]) => {
            if (key.includes('-Comment')) {
              let actualKey = key.split('-')[0]
              let correspondingObj = acc.find(
                (obj) => obj.qi === Number(actualKey.slice(1))
              )

              console.log('安全檢查 otherAnswers', otherAnswers)

              // 安全檢查 otherAnswers
              if (correspondingObj) {
                if (
                  otherAnswers[actualKey] &&
                  otherAnswers[actualKey].otherIndex !== undefined
                ) {
                  let otherKey = otherAnswers[actualKey].otherIndex
                  correspondingObj.other[otherKey] = value
                }
              }

              return acc
            }

            let ans = []

            if (answerIndexes[key] || answerIndexes[key] === 0) {
              // console.log('value', key, ':', value)
              // 單選多選 有就放
              if (Array.isArray(answerIndexes[key])) {
                ans = answerIndexes[key]
                // console.log('Array')
              } else if (typeof value === 'object' && !Array.isArray(value)) {
                ans = matrixAnswers[key]
                // console.log('矩陣 1111', matrixAnswers)
              } else if (value === 'other') {
                // 單選 other
                ans = [otherAnswers[key].otherIndex]
                // console.log('other', key)
              } else if (value === 'none') {
                // 單選 none
                ans = [noneAnswers[key].noneIndex]
                // console.log('none', key)
              } else if (answerIndexes[key].title) {
                ans = [answerIndexes[key].index]
                // console.log('帶入', key)
              } else {
                ans = [answerIndexes[key]]
                // console.log('單選', key, answerIndexes[key])
              }
            } else if (value === 'other') {
              // 單選 other
              ans = [otherAnswers[key].otherIndex]
              // console.log('other', key)
            } else if (value === 'none') {
              // 單選 none
              ans = [noneAnswers[key].noneIndex]
            } else if (typeof value === 'object' && !Array.isArray(value)) {
              // 矩陣
              ans = matrixAnswers[key]
              // console.log('矩陣 2222', matrixAnswers[key])
              // console.log('矩陣 key', key)
            } else if (Array.isArray(value)) {
              // 多選 other & none
              ans = value.map((v) =>
                v === 'other'
                  ? otherAnswers[key].otherIndex
                  : v === 'none'
                  ? noneAnswers[key].noneIndex
                  : v
              )
            } else {
              // 開放題
              ans = [value]
            }

            // 帶入帶出交答案小工具
            function findValueByKey(object, key) {
              for (const objKey in object) {
                if (object[objKey] === key) {
                  return objKey
                }
              }
              return null
            }

            // 安全檢查 choicesOrderRef
            console.log('choiceIndexMapRef', choiceIndexMapRef)
            console.log('choiceIndexMapRef.current', choiceIndexMapRef?.current)
            console.log(
              'choiceIndexMapRef.current[key]',
              choiceIndexMapRef?.current?.[key]
            )
            console.log(
              'choiceIndexMapRef.current[key][0]',
              choiceIndexMapRef?.current?.[key]?.[0]
            )
            console.log('submitanswer choiceIndexMapRef', key)
            console.log(
              '檢查 choiceIndexMapRef:',
              key,
              choiceIndexMapRef.current[key]
            )

            const ordValue = choicesOrderRef.current[key] || []
            console.log('安全檢查 ordValue', ordValue)
            let finalQuestionItem = {
              pi: pid,
              ans: ans,
              ord: ordValue,
              other: {},
              ct: timestamps[key],
              qi: Number(key.slice(1)),
              ...(choiceIndexMapRef?.current[key][0]?.title
                ? {
                    bringing_options: {
                      options: Object.values(choiceIndexMapRef.current[key])
                        .filter((v) => v.title)
                        .map((v) => ({
                          o_type: 0,
                          na: v.title,
                          origin_op: v.origin_op,
                          origin_qi: v.origin_qi,
                        })),
                    },
                  }
                : {}),
            }

            // ✅ 特殊處理：如果 ans 包含 'none' 且有 bringing_options
            if (
              finalQuestionItem.bringing_options &&
              Array.isArray(ans) &&
              ans.includes('none')
            ) {
              console.log(
                `🎯 ${key} 檢測到 ans 包含 'none'，從 bringing_options 取得索引`
              )

              // 找到 bringing_options 中 "以上皆無" 選項的位置
              const noneOptionIndex =
                finalQuestionItem.bringing_options.options.findIndex(
                  (option) =>
                    option.na.includes('以上皆') ||
                    option.na.includes('皆無') ||
                    option.na.includes('皆非')
                )

              console.log('noneOptionIndex', noneOptionIndex)

              if (noneOptionIndex !== -1) {
                // 將 ans 中的 'none' 替換為在 bringing_options 中的索引
                finalQuestionItem.ans = ans.map((a) =>
                  a === 'none' ? noneOptionIndex : a
                )

                console.log(
                  `✅ ${key} 修正 ans 從 'none' 到索引 ${noneOptionIndex}:`,
                  finalQuestionItem.ans
                )
              } else {
                console.warn(`⚠️ ${key} 在 bringing_options 中找不到 none 選項`)
              }
            }

            acc.push(finalQuestionItem)

            return acc
          },
          []
        )
        transformedData.current = newTransformedData
        console.log('newTransformedData useSubmitAnswer', newTransformedData)
      }
    }
    // let isUploadSuccessful = false

    // HA 問卷填答完成任務狀態更新
    const generateVerify = (postBody, apiSecret) => {
      // 將JSON body壓縮(移除空白)
      const compressedBody = JSON.stringify(JSON.parse(postBody))

      // 使用HMAC-SHA256算法進行加密
      const hmac = CryptoJS.HmacSHA256(compressedBody, apiSecret)

      // 將結果轉換為Base64
      const verify = CryptoJS.enc.Base64.stringify(hmac)

      return verify
    }

    const handleComplete = async (sender, options) => {
      // Wait for the file uploads to complete
      while (!uploadCompleted) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      if (!uploadCompleted || uploadFailed) {
        // 如果上传失败，则不执行提交操作
        return
      } else {
        let finalAnswers = [...transformedData.current]
        console.log('finalAnswers', finalAnswers)

        if (pid === 'P240719175039533') {
          finalAnswers.push({
            pi: 'P240719175039533',
            ans: [`https://report.dkabio.com/report/light/demo/result/${ac}`],
            ord: [],
            other: {},
            ct: Date.now(),
            qi: 0,
          })
        }
        const sortedAns = finalAnswers.sort(function (a, b) {
          return a.qi - b.qi
        })
        let surveyData = {
          pid: pid,
          answers: sortedAns,
        }

        console.log('surveyData', surveyData)
        if (surveyData.answers.length === 0) {
          console.log('沒有答案，跳過提交')
          return
        }

        axios
          .post(host + SubmitAnswersUrl, surveyData, {
            headers: {
              'Content-Type': 'text/plain; charset=UTF-8',
              Accept: '*/*',
              ac: ac,
              tk: tk,
            },
            timeout: 60000, // 60秒 timeout (提交時間可能較長)
          })
          .then((res) => {
            if (res.data.code === '02000') {
              if (endSurveyMsg) {
                endSurveyMsg(sender)
              }
              if (pid === 'P240430105824' || pid === 'P240430105853') {
                navigate(`/computex-2024-success`)
              } else if (
                pid === 'P250423112413307' ||
                pid === 'P250424001910598'
              ) {
                navigate(`/computex-2025-success`)
              } else if (
                pid === 'P240614183901' ||
                pid === 'P240719175039533'
              ) {
                window.location.href = `https://report.dkabio.com/report/light/demo/result/${ac}`
              } else if (pid === 'P240827173131128') {
                navigate(`/ha/${pid}/dkabio-success`)
              } else if (haAccessToken && haMemberMissionId) {
                setIsDkabio(true)
                // 邁達特健康問卷填答完成任務狀態更新
                const missionRequestBody = {
                  access_token: haAccessToken,
                  member_mission_id: haMemberMissionId,
                }
                const postBody = JSON.stringify(missionRequestBody)
                // navigate(`/dkabio-success`)
                // console.log('postBody', postBody)
                axios
                  .post(
                    `${haApiUrl}/api/web/mission/updateQuestionnaire`,
                    missionRequestBody,
                    {
                      headers: {
                        'Content-Type': ' application/json',
                        Accept: '*/*',
                        key: haApiKey,
                        verify: generateVerify(postBody, haApiSecret),
                      },
                    }
                  )
                  .then((res) => {
                    // console.log('邁達特健康問卷回覆', res)
                    if (res.data.status) {
                      navigate(`/ha/${pid}/dkabio-success`)
                    } else {
                      navigate(`/ha/pid=${pid}/submitFail`)
                    }
                  })
                  .catch((e) => {
                    console.log(e)
                    sendDiscordNotification(
                      `邁達特健康問卷填答完成任務狀態更新錯誤 \n Error: ${e}  \n missionRequestBody: ${JSON.stringify(
                        missionRequestBody
                      )} `
                    )
                  })
              } else {
                navigate(`/${pid}/success`)
              }
            }
            // 03102 答案格式與題目不符
            if (res.data.code === '05000') {
              if (haAccessToken && haMemberMissionId) {
                navigate(`/ha/pid=${pid}/submitFail`)
              } else {
                navigate(`/pid=${pid}/submitFail`)
              }
              sendDiscordNotification(
                `EZC Web Submit Error \n Error: 內文格式不符 ${
                  res.data.code
                }  \n surveyData: ${JSON.stringify(
                  surveyData
                )}  \n ac: ${ac} \n tk: ${tk} `
              )
            } else if (res.data.code === '03044') {
              navigate(`/pid=${pid}/done`)
            } else if (res.data.code !== '02000') {
              if (haAccessToken && haMemberMissionId) {
                navigate(`/ha/pid=${pid}/submitFail`)
              } else {
                navigate(`/pid=${pid}/submitFail`)
              }

              sendDiscordNotification(
                `EZC Web Submit Error \n Error: 問卷送出有問題${
                  res.data.code
                }  \n surveyData: ${JSON.stringify(
                  surveyData
                )}  \n ac: ${ac} \n tk: ${tk} `
              )
            }
          })
          .catch((e) => {
            console.log(e)

            // 檢查是否為網路錯誤、timeout 或請求被中止
            const isNetworkError =
              e.code === 'ECONNABORTED' || // axios timeout
              e.code === 'ERR_CANCELED' || // 請求被取消
              e.message.includes('timeout') || // 一般 timeout 訊息
              e.message.includes('aborted') || // 請求被中止
              e.message.includes('canceled') || // 請求被取消
              e.message.includes('Network Error') || // 網路錯誤
              !e.response // 沒有伺服器回應

            // 特別檢查是否為用戶主動中止
            const isUserAborted =
              e.code === 'ERR_CANCELED' ||
              e.message.includes('aborted') ||
              e.message.includes('canceled')

            // 用戶主動中止請求的處理
            if (isUserAborted) {
              console.log('問卷提交請求被用戶中止')
              return // 不顯示 alert，不導航，不打 Discord
            }

            // 網路錯誤或 timeout 的處理
            if (isNetworkError) {
              alert('網路連線異常或提交逾時，請檢查網路狀況後重新提交')
              // 不導航到失敗頁面，讓用戶可以重試
              return
            }

            // 檢查特定的 HTTP 狀態碼錯誤
            if (e.response && e.response.status) {
              const status = e.response.status
              const errorCode = e.response.data?.code

              if (status === 401) {
                // 401 Unauthorized - 通常是 token 過期
                alert('登入權限已過期，請重新整理頁面後重新填寫')
              } else if (status === 500) {
                // 500 Internal Server Error - 伺服器錯誤
                alert('伺服器發生錯誤，請稍後再試或聯絡客服')
              } else if (errorCode === '04006') {
                // Invalid access
                alert('存取權限已過期，請重新整理頁面後重新填寫')
              } else {
                // 其他 API 錯誤
                alert('問卷提交時發生錯誤，請重新提交或聯絡客服')
              }
            } else {
              // 其他未知錯誤
              alert('提交時發生未知錯誤，請重新提交或聯絡客服')
            }

            if (haAccessToken && haMemberMissionId) {
              navigate(`/ha/pid=${pid}/submitFail`)
            } else {
              navigate(`/pid=${pid}/submitFail`)
            }

            // 打 Discord 通知（排除用戶主動中止和純網路問題）
            if (!isUserAborted && !isNetworkError) {
              sendDiscordNotification(
                `EZC Web Submit Error \n Error: ${e} \n surveyData: ${JSON.stringify(
                  surveyData
                )} \n Response: ${e.response?.data?.msg} \n Response Code: ${
                  e.response?.data?.code
                } \n Status: ${
                  e.response?.status
                } \n ac: ${ac} \n tk: ${tk} \n Message: ${e.message}`
              )
            }
          })
          .finally(() => {
            setIsLoading(false)

            const shareArea = document.querySelector('.shareArea')
            if (shareArea) {
              shareArea.style.display = 'block'
            }
          })

        console.log('繳交答案', surveyData)
      }
    }

    // 監聽題目的可見度變化，一隱藏就立刻把答案清空
    const handleVisibleChanged = (sender, options) => {
      // options.question 是發生可見度改變的題目
      // options.visible 是新的可見度狀態
      if (!options.visible) {
        // console.log(`題目 ${options.question.name} 被隱藏，準備清除其答案`)
        options.question.clearValue()

        // 如果該題有連帶的 Comment 題(其他)，也要一併清除
        const commentName = `${options.question.name}-Comment`
        const commentQuestion = sender.getQuestionByName(commentName)
        if (commentQuestion) {
          commentQuestion.clearValue()
        }
      }
    }

    // 添加事件監聽器
    survey.onAfterRenderQuestion.add(handleAfterRenderQuestion)
    survey.onValueChanged.add(handleValueChanged)
    survey.onCurrentPageChanged.add(handlePageChanged)
    survey.onCompleting.add(handleCompleting)
    survey.onComplete.add(handleComplete)
    survey.onVisibleChanged.add(handleVisibleChanged)
    // 清理函數
    return () => {
      survey.onAfterRenderQuestion.remove(handleAfterRenderQuestion)
      survey.onValueChanged.remove(handleValueChanged)
      survey.onCurrentPageChanged.remove(handlePageChanged)
      survey.onCompleting.remove(handleCompleting)
      survey.onComplete.remove(handleComplete)
      survey.onVisibleChanged.remove(handleVisibleChanged)
    }
  }, [survey, pid, ac, tk, enableProgressSaving, requiresLogin, userIdRef])

  return {}
}
